/**
 * @file attribution.js
 * @description First-touch marketing attribution + anonymous identity for the
 *   acquisition funnel. Captures UTM params, referrer, and landing path on the
 *   visitor's first page load (before any signup) and persists them in
 *   localStorage. A stable anon_id lets guest conversion events be recorded
 *   pre-signup and then "claimed" — linked to the real user_id — once they
 *   create an account, via the claim_anon_events RPC.
 *
 *   Privacy: this is the SOURCE OF TRUTH for attribution and stays in Supabase.
 *   None of it is sent to GA4 (see lib/analytics.js). No PII is captured here —
 *   only campaign params, an opaque random id, and the entry path.
 * @module lib/attribution
 */

const ANON_KEY = "ac_anon_id";
const ATTR_KEY = "ac_attr";

function ls() {
  try { return window.localStorage; } catch { return null; }
}

function uuid() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch { /* ignore */ }
  // Fallback (non-crypto) — fine for an opaque analytics id.
  return "axxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.floor((performance.now() * 1000) % 16) + Math.floor(Date.parse(new Date().toString()) % 16)) % 16;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** Stable opaque id for this browser; created on first call. */
export function getAnonId() {
  const store = ls();
  if (!store) return null;
  let id = store.getItem(ANON_KEY);
  if (!id) {
    id = uuid();
    try { store.setItem(ANON_KEY, id); } catch { /* ignore */ }
  }
  return id;
}

/**
 * Record first-touch attribution once (does not overwrite an existing record).
 * Call as early as possible on app load. Referrer is stored only when it's an
 * EXTERNAL site (self-referrals are noise).
 */
export function captureFirstTouch() {
  const store = ls();
  if (!store || typeof window === "undefined") return;
  if (store.getItem(ATTR_KEY)) return; // first-touch already recorded

  let params;
  try { params = new URLSearchParams(window.location.search); } catch { params = new URLSearchParams(); }

  let referrer = "";
  try {
    const ref = document.referrer || "";
    if (ref && new URL(ref).host !== window.location.host) referrer = ref;
  } catch { /* ignore */ }

  const attr = {
    utm_source:   params.get("utm_source")   || "",
    utm_medium:   params.get("utm_medium")   || "",
    utm_campaign: params.get("utm_campaign") || "",
    // Cold-outreach links carry the prospect id here, so a signup ties to the exact prospect.
    utm_content:  (params.get("utm_content") || "").slice(0, 128),
    landing_path: window.location.pathname || "/",
    referrer,
  };

  try { store.setItem(ATTR_KEY, JSON.stringify(attr)); } catch { /* ignore */ }
}

// ── This visit's landing (not first-touch) ──────────────────────────────────
// First-touch is stored once per browser, so a returning visitor who comes back
// through an ad would still be credited to their original source. Case views
// need THIS visit's source, captured at boot before any in-app URL rewrite (the
// /share/<id>?utm_* landing replaces itself with "/"). In memory only.
let sessionLanding = null;

/** Where an in-app browser visit came from, from the user agent. */
export function inAppBrowser(ua = typeof navigator !== "undefined" ? navigator.userAgent : "") {
  const s = String(ua || "");
  if (/Instagram/i.test(s)) return "instagram";
  if (/FBAN|FBAV|FB_IAB/i.test(s)) return "facebook";
  if (/LinkedInApp/i.test(s)) return "linkedin";
  return null;
}

/** Snapshot this visit's landing path, utm_* and referrer host. Call at boot. */
export function captureSessionLanding() {
  if (typeof window === "undefined") return;
  let params;
  try { params = new URLSearchParams(window.location.search); } catch { params = new URLSearchParams(); }
  let referrerHost = "";
  try {
    const ref = document.referrer || "";
    const host = ref ? new URL(ref).host : "";
    if (host && host !== window.location.host) referrerHost = host;
  } catch { /* ignore */ }
  sessionLanding = {
    landing_path: window.location.pathname || "/",
    utm_source:   (params.get("utm_source")   || "").slice(0, 64),
    utm_medium:   (params.get("utm_medium")   || "").slice(0, 64),
    utm_campaign: (params.get("utm_campaign") || "").slice(0, 64),
    utm_content:  (params.get("utm_content")  || "").slice(0, 64),
    referrer_host: referrerHost,
    in_app:       inAppBrowser(),
  };
}

/** This visit's landing snapshot (empty object if not captured). */
export function getSessionLanding() {
  return sessionLanding || {};
}

/**
 * The source of this visit as one short label, for case-view reporting:
 * utm_source when tagged, else the in-app browser, else the referrer host,
 * else "direct".
 */
export function visitSourceLabel(landing = getSessionLanding()) {
  if (landing.utm_source) return landing.utm_source.toLowerCase();
  if (landing.in_app) return landing.in_app;
  const h = (landing.referrer_host || "").toLowerCase();
  if (h.includes("instagram")) return "instagram";
  if (h.includes("facebook") || h === "fb.com" || h.endsWith(".fb.com")) return "facebook";
  if (h.includes("google")) return "google";
  if (h.includes("bing")) return "bing";
  if (h.includes("yahoo")) return "yahoo";
  if (h.includes("linkedin")) return "linkedin";
  if (h) return h;
  return "direct";
}

/** First-touch attribution record (empty object if none captured yet). */
export function getAttribution() {
  const store = ls();
  if (!store) return {};
  try { return JSON.parse(store.getItem(ATTR_KEY) || "{}"); } catch { return {}; }
}

/**
 * The outreach prospect id (utm_content) for the current visit: the live URL wins (so a
 * visitor who already had a first-touch record still ties to the prospect they just clicked
 * from), else the first-touch record. Returns null when there is none. Opaque id, no PII.
 */
export function getUtmContent() {
  try {
    const cur = new URLSearchParams(window.location.search).get("utm_content");
    if (cur) return cur.slice(0, 128);
  } catch { /* ignore */ }
  return getAttribution().utm_content || null;
}

/**
 * Per-event context for the events table: the stable anon_id, first-touch UTM
 * (so each event carries campaign attribution), the CURRENT page path, and
 * referrer.
 */
export function getEventContext() {
  const attr = getAttribution();
  return {
    anon_id:      getAnonId(),
    utm_source:   attr.utm_source   || null,
    utm_medium:   attr.utm_medium   || null,
    utm_campaign: attr.utm_campaign || null,
    source_page:  (typeof window !== "undefined" && window.location.pathname) || null,
    referrer:     attr.referrer     || null,
  };
}

/**
 * After signup/sign-in: link this browser's pre-signup guest events to the user
 * and stamp first-touch attribution on their profile. Idempotent and safe to
 * call on every sign-in. `interest` latches the tracker / case-alert intent.
 *
 * @param {import('@supabase/supabase-js').SupabaseClient} supabase
 * @param {string} userId
 * @param {{ interested_in_tracker?: boolean, interested_in_case_alerts?: boolean }} [interest]
 * @returns {Promise<number>} number of guest events claimed
 */
export async function claimAnonEvents(supabase, userId, interest = {}) {
  if (!supabase || !userId) return 0;
  const anonId = getAnonId();
  const attr = getAttribution();
  const p_attr = {
    utm_source:   attr.utm_source   || null,
    utm_medium:   attr.utm_medium   || null,
    utm_campaign: attr.utm_campaign || null,
    utm_content:  attr.utm_content  || null,
    landing_path: attr.landing_path || null,
    referrer:     attr.referrer     || null,
    interested_in_tracker:     !!interest.interested_in_tracker,
    interested_in_case_alerts: !!interest.interested_in_case_alerts,
  };
  try {
    const { data, error } = await supabase.rpc("claim_anon_events", { p_anon_id: anonId, p_attr });
    if (error) { console.warn("[attribution] claim failed:", error.message); return 0; }
    return data || 0;
  } catch (e) {
    console.warn("[attribution] claim threw:", e?.message || e);
    return 0;
  }
}
